/**
 * ExtractActionItemsDialog — scan the document for commitments that
 * weren't captured as formal action items. The user reviews proposals
 * and accepts the ones to apply; accepted items are appended to
 * meeting.structuredContent.actionItems and autosave PATCHes.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import type { StructuredContent } from '@ojb/shared';

interface ProposedAction {
  description: string;
  assigneeName?: string;
  dueDate?: string;
  sourceQuote: string;
  confidence: 'high' | 'medium' | 'low';
}

interface UsageMeta {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
}

interface Meeting {
  id: string;
  structuredContent: Record<string, unknown>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  /**
   * Called with the next structuredContent (with accepted action
   * items appended). The caller is expected to update its draft
   * state so autosave picks up the change.
   */
  onApply: (next: StructuredContent) => void;
}

export function ExtractActionItemsDialog({
  isOpen,
  onClose,
  meeting,
  onApply,
}: Props) {
  // Edit state per proposal — user can tweak description / assignee
  // / due date inline before accepting.
  const [drafts, setDrafts] = useState<Array<ProposedAction & { accepted: boolean }>>([]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ proposed: ProposedAction[]; usage: UsageMeta }>(
        `/meetings/${meeting.id}/extract-action-items`,
      ),
    onSuccess: (data) => {
      // Default-accept high-confidence proposals; let user opt in for
      // medium and low.
      setDrafts(
        data.proposed.map((p) => ({
          ...p,
          accepted: p.confidence === 'high',
        })),
      );
    },
  });

  // Fire the mutation exactly once per open cycle.
  useEffect(() => {
    if (isOpen && !mutation.data && !mutation.isPending && !mutation.isError) {
      mutation.mutate();
    }
    if (!isOpen) {
      mutation.reset();
      setDrafts([]);
    }
  }, [isOpen]);

  const acceptedCount = drafts.filter((d) => d.accepted).length;

  const apply = () => {
    const accepted = drafts.filter((d) => d.accepted);
    if (accepted.length === 0) {
      onClose();
      return;
    }
    const current = meeting.structuredContent as unknown as StructuredContent;
    const existing = current.actionItems ?? [];
    const next: StructuredContent = {
      ...current,
      actionItems: [
        ...existing,
        ...accepted.map((a) => ({
          description: a.description.trim(),
          assigneeName: a.assigneeName?.trim() || undefined,
          dueDate: a.dueDate || undefined,
          status: 'open' as const,
        })),
      ],
    };
    onApply(next);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Extract missing action items"
    >
      <ModalHeader
        title="Extract missing action items"
        subtitle="Claude scans the document for commitments buried in discussion that aren't already in your action-items list. Accept the ones you want to add."
        onClose={onClose}
      />
      <ModalBody>
        {mutation.isPending && (
          <div className="py-10 text-center text-sm italic text-charcoal/60">
            Claude is scanning for buried commitments…
          </div>
        )}
        {mutation.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'Extract failed'}
          </div>
        )}
        {mutation.data && drafts.length === 0 && (
          <div className="rounded-2xl border border-dashed border-charcoal/20 p-6 text-center text-sm italic text-charcoal/60">
            Nothing new found. Every commitment in this meeting is
            already captured in the action items list.
          </div>
        )}
        {drafts.length > 0 && (
          <ul className="space-y-3">
            {drafts.map((d, i) => (
              <li
                key={i}
                className={
                  'rounded-2xl border p-4 transition-colors ' +
                  (d.accepted
                    ? 'border-pine/40 bg-pine/5'
                    : 'border-charcoal/15 bg-white')
                }
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={d.accepted}
                    onChange={(e) =>
                      setDrafts((arr) => {
                        const next = [...arr];
                        next[i] = { ...next[i], accepted: e.target.checked };
                        return next;
                      })
                    }
                    className="mt-1 h-4 w-4 accent-pine"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <ConfidencePill level={d.confidence} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                        Description
                      </label>
                      <input
                        {...NO_AUTOFILL}
                        type="text"
                        value={d.description}
                        onChange={(e) =>
                          setDrafts((arr) => {
                            const next = [...arr];
                            next[i] = { ...next[i], description: e.target.value };
                            return next;
                          })
                        }
                        className="mt-1 w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                          Assignee
                        </label>
                        <input
                          {...NO_AUTOFILL}
                          type="text"
                          value={d.assigneeName ?? ''}
                          onChange={(e) =>
                            setDrafts((arr) => {
                              const next = [...arr];
                              next[i] = {
                                ...next[i],
                                assigneeName: e.target.value,
                              };
                              return next;
                            })
                          }
                          className="mt-1 w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                          Due date
                        </label>
                        <input
                          {...NO_AUTOFILL}
                          type="date"
                          value={d.dueDate ?? ''}
                          onChange={(e) =>
                            setDrafts((arr) => {
                              const next = [...arr];
                              next[i] = {
                                ...next[i],
                                dueDate: e.target.value || undefined,
                              };
                              return next;
                            })
                          }
                          className="mt-1 w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
                        />
                      </div>
                    </div>
                    <details className="text-[11px] text-charcoal/60">
                      <summary className="cursor-pointer hover:text-pine">
                        Source quote
                      </summary>
                      <div className="mt-1 rounded border border-charcoal/10 bg-cloud/40 p-2 italic">
                        &ldquo;{d.sourceQuote}&rdquo;
                      </div>
                    </details>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
      <ModalFooter className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/60">
          {acceptedCount > 0
            ? `${acceptedCount} selected`
            : drafts.length > 0
              ? 'None selected'
              : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={acceptedCount === 0}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
          >
            Add {acceptedCount > 0 ? `${acceptedCount} item${acceptedCount === 1 ? '' : 's'}` : 'selected'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

function ConfidencePill({ level }: { level: 'high' | 'medium' | 'low' }) {
  const map = {
    high: 'bg-pine/10 text-pine',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-charcoal/10 text-charcoal/70',
  };
  return (
    <span
      className={
        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ' +
        map[level]
      }
    >
      {level} confidence
    </span>
  );
}
